import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');
  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '91tv',
    url: 'https://www.91tv.com',
    description: '91tv高清视频资源聚合平台，提供海量影视、综艺、动漫及短视频资源。',
    foundingDate: '2018',
    contactPoint: [
      {
        '@type': 'ContactPoint',
        telephone: '+86-400-888-9191',
        contactType: 'customer service',
        availableLanguage: ['Chinese'],
      },
      {
        '@type': 'ContactPoint',
        email: 'contact@91tv.com',
        contactType: 'business',
        availableLanguage: ['Chinese'],
      },
    ],
    address: {
      '@type': 'PostalAddress',
      addressLocality: '深圳',
      addressRegion: '广东',
      addressCountry: 'CN',
      streetAddress: '深圳市南山区科技园南区',
    },
  };

  const milestones = [
    { year: '2018', title: '91tv正式上线', desc: '以高清视频聚合为核心定位，91tv官网正式面向用户开放。' },
    { year: '2019', title: '用户突破1000万', desc: '凭借优质的内容和流畅的体验，91tv入口迅速获得用户认可。' },
    { year: '2020', title: '推出移动端应用', desc: '91tv适配多终端，移动端上线，用户可随时随地在线观看高清视频。' },
    { year: '2021', title: '内容生态升级', desc: '引入更多正版影视、综艺、动漫资源，91tv高清视频内容库大幅扩充。' },
    { year: '2022', title: '用户突破3000万', desc: '91tv在线观看体验持续优化，用户规模高速增长。' },
    { year: '2023', title: 'AI智能推荐上线', desc: '基于大数据的智能推荐系统，为用户提供更精准的内容推送。' },
    { year: '2024', title: '全面升级4K画质', desc: '91tv资源下载和在线播放全面支持4K超清画质，观影体验再升级。' },
  ];

  const teamMembers = [
    { name: '张明远', role: '创始人 & CEO', desc: '前腾讯视频产品总监，拥有15年互联网视频行业经验。' },
    { name: '李思涵', role: 'CTO', desc: '前阿里巴巴技术专家，专注于大规模分布式系统和视频编解码技术。' },
    { name: '王雨晴', role: '内容总监', desc: '前爱奇艺内容运营负责人，深耕影视内容领域10年。' },
    { name: '陈浩然', role: '产品总监', desc: '前字节跳动高级产品经理，擅长用户增长和产品体验设计。' },
  ];

  const testimonials = [
    { name: '刘先生', role: '资深影迷', content: '用了91tv三年了，资源真的非常全，画质也很棒。更新速度快，新剧上线第一时间就能看到，强烈推荐！', rating: 5 },
    { name: '张女士', role: '全职妈妈', content: '平时带孩子没时间去电影院，91tv成了我最重要的娱乐方式。操作简单，老人小孩都能轻松使用。', rating: 5 },
    { name: '陈同学', role: '大学生', content: '91tv的国漫频道太赞了！《斗罗大陆》、《完美世界》每周准时更新，画质也很高清。', rating: 5 },
    { name: '赵先生', role: 'IT工程师', content: '技术层面来说，91tv的加载速度和播放流畅度都做得很好。多终端同步观看记录的功能也很实用。', rating: 4 },
    { name: '孙女士', role: '自由职业者', content: '综艺节目资源很齐全，《乘风破浪的姐姐》和《奔跑吧兄弟》每期都不落下。弹幕功能也很有意思。', rating: 5 },
    { name: '周先生', role: '中学教师', content: '91tv的纪录片频道内容很有深度，在课堂上也会推荐学生看一些优质的纪录片。希望平台越做越好！', rating: 5 },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <Navbar />
      <main className="min-h-screen pt-16">
        {/* Page Header */}
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-6">
            <Breadcrumb />
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mt-2">
              关于91tv
            </h1>
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-6 text-center">
              品牌介绍
            </h2>
            <div className="text-foreground-700 leading-relaxed space-y-4 text-sm">
              <p>
                <strong>91tv</strong>成立于2018年，是国内领先的高清视频资源聚合平台。91tv官方入口致力于为广大用户提供海量优质的影视、综艺、动漫及短视频资源，支持PC、平板、手机等多终端流畅播放，让用户随时随地享受极速、清晰、免费的在线观看体验。
              </p>
              <p>
                91tv官网始终坚持以用户体验为核心，不断优化产品功能。无论是热门电视剧、院线大片，还是高分综艺、精品国漫，用户都可以通过91tv入口轻松找到想看的内容。平台采用先进的视频编解码技术，确保高清画质的同时保持极低的带宽消耗。
              </p>
              <p>
                目前，91tv的月活跃用户已突破<strong>5000万</strong>，内容库涵盖<strong>10万+</strong>视频资源，覆盖电视剧、电影、综艺、动漫、纪录片、短剧、体育、音乐八大核心品类。91tv最新地址持续更新中，确保用户稳定访问。
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-10">
              {[
                { icon: 'ri-user-star-line', label: '5000万+', sub: '月活跃用户' },
                { icon: 'ri-video-line', label: '10万+', sub: '视频资源' },
                { icon: 'ri-shield-check-line', label: '99.9%', sub: '服务可用率' },
                { icon: 'ri-emotion-happy-line', label: '4.9分', sub: '用户综合评分' },
              ].map((item) => (
                <div key={item.sub} className="text-center p-5 rounded-xl bg-background-100/70 border border-background-200/70">
                  <i className={`${item.icon} text-2xl text-primary-500`}></i>
                  <div className="text-xl font-bold text-foreground-900 mt-2 font-label">{item.label}</div>
                  <div className="text-xs text-foreground-500 mt-1">{item.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Development Timeline */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-10 text-center">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300 md:-translate-x-px"></div>
              <div className="space-y-8">
                {milestones.map((item, idx) => (
                  <div key={item.year} className={`relative flex items-start gap-4 md:gap-8 ${idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className="hidden md:block flex-1"></div>
                    <div className="relative z-10 w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-500 text-white text-xs font-bold font-label">
                      {item.year.slice(2)}
                    </div>
                    <div className="flex-1 bg-background-50 rounded-xl p-5 border border-background-200/70">
                      <span className="text-xs text-primary-500 font-semibold">{item.year}</span>
                      <h3 className="text-sm font-semibold text-foreground-800 mt-1">{item.title}</h3>
                      <p className="text-xs text-foreground-500 mt-1 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-[1100px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-10 text-center">
              核心团队
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {teamMembers.map((member) => (
                <div key={member.name} className="bg-background-100/70 rounded-xl p-5 border border-background-200/70 text-center">
                  <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-primary-500/20 to-accent-500/20 flex items-center justify-center">
                    <i className="ri-user-line text-2xl text-primary-500"></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-800 mt-3">{member.name}</h3>
                  <p className="text-xs text-primary-500 mt-0.5">{member.role}</p>
                  <p className="text-xs text-foreground-500 mt-2 leading-relaxed">{member.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* User Testimonials */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-[1100px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-10 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((t, idx) => (
                <div key={idx} className="bg-background-50 rounded-xl p-5 border border-background-200/70">
                  <div className="flex gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className={`text-sm ${i < t.rating ? 'ri-star-fill text-amber-400' : 'ri-star-line text-foreground-300'}`}></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">"{t.content}"</p>
                  <div className="flex items-center gap-3 mt-4 pt-3 border-t border-background-200/70">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs text-primary-600 font-semibold">{t.name.charAt(0)}</span>
                    </div>
                    <div>
                      <div className="text-xs font-medium text-foreground-800">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-[900px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-10 text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
              {[
                { icon: 'ri-mail-line', title: '电子邮件', content: 'contact@91tv.com', desc: '商务合作与媒体咨询' },
                { icon: 'ri-phone-line', title: '客服热线', content: '400-888-9191', desc: '工作日 9:00-18:00' },
                { icon: 'ri-map-pin-line', title: '公司地址', content: '深圳市南山区科技园南区', desc: '91tv总部' },
              ].map((item) => (
                <div key={item.title} className="bg-background-100/70 rounded-xl p-5 border border-background-200/70 text-center">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-50">
                    <i className={`${item.icon} text-xl text-primary-500`}></i>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-800 mt-3">{item.title}</h3>
                  <p className="text-sm text-foreground-700 mt-1">{item.content}</p>
                  <p className="text-xs text-foreground-500 mt-1">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="max-w-[500px] mx-auto">
              <h3 className="text-sm font-semibold text-foreground-800 mb-4 text-center">给我们留言</h3>
              <form
                className="space-y-3"
                data-readdy-form="about-contact-form"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const formData = new FormData(form);
                  const honeypotVal = (formData.get('website_alt') as string || '').trim();
                  if (honeypotVal) {
                    setFormStatus('success');
                    setFormMessage('留言已提交，我们会尽快回复您！');
                    return;
                  }
                  formData.delete('website_alt');
                  setFormStatus('loading');
                  setFormMessage('');
                  try {
                    const resp = await fetch('https://readdy.ai/api/form/d9snkf5lbrt1qcgaj6ig', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                      body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
                    });
                    const responseText = await resp.text();
                    let parsed: any = {};
                    try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
                    if (resp.ok && parsed?.code === 'OK') {
                      setFormStatus('success');
                      setFormMessage('留言已提交，我们会尽快回复您！');
                      form.reset();
                    } else {
                      const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败';
                      setFormStatus('error');
                      setFormMessage(serverMsg.includes('spam') ? '提交失败，请稍后重试' : serverMsg);
                    }
                  } catch {
                    setFormStatus('error');
                    setFormMessage('网络错误，请稍后重试');
                  }
                }}
              >
                <input type="text" name="name" placeholder="您的姓名" className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors" />
                <input type="email" name="email" placeholder="您的邮箱" className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors" />
                <textarea name="message" placeholder="请输入您的留言（最多500字）" maxLength={500} rows={4} className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"></textarea>
                <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                <button type="submit" disabled={formStatus === 'loading'} className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap text-sm disabled:opacity-60">
                  {formStatus === 'loading' ? '提交中...' : '提交留言'}
                </button>
                {formMessage && (
                  <p className={`text-xs text-center mt-2 ${formStatus === 'success' ? 'text-accent-600' : 'text-red-500'}`}>
                    {formMessage}
                  </p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}